import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, InputAdornment, MenuItem, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LanguageFlag from '../common/LanguageFlag';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareSelectField from '../common/SalovareSelectField';
import ScrollableDialog from '../common/ScrollableDialog';
import SwitchField from '../common/SwitchField';
import { buildAdornmentTextFieldSx, fieldLabelRowSx } from '../common/formFieldStyles';
import ServicePackageCheckboxGroup from '../service-packages/ServicePackageCheckboxGroup';
import ServicePrepaymentFields from '../services/ServicePrepaymentFields';
import VatClassificationFields from '../common/VatClassificationFields';
import ProLockedSection from '../billing/ProLockedSection';
import { getLocalizedValue } from '../../constants/Languages';
import { DepositType, normalizeDepositType, normalizePrepaymentMode } from '../../constants/PrepaymentMode';
import { getCurrencySymbol } from '../../utils/currencyUtils';
import { isFreeServicePrice } from '../../utils/freeServiceUtils';
import { validateServicePrepayment } from '../../utils/prepaymentUtils';
import { readVatClassification, validateVatClassification } from '../../utils/vatClassification';
import { BRAND } from '../../theme/brandTokens';

const MIN_DURATION = 5;
const MAX_DURATION = 480;
const DURATION_STEP = 5;
const FALLBACK_LANGUAGES = ['en'];

const buildLocalizedFields = (supportedLanguages, values = {}) => (
  Object.fromEntries(supportedLanguages.map((language) => [language, values?.[language] || '']))
);

const buildInitialCombo = (supportedLanguages, combo) => ({
  id: combo?.id ?? null,
  name: buildLocalizedFields(supportedLanguages, combo?.name),
  duration: combo?.duration ?? '',
  price: combo?.price ?? '',
  disabled: Boolean(combo?.disabled),
  serviceIds: Array.isArray(combo?.serviceIds) ? [...combo.serviceIds] : [],
  prepaymentMode: normalizePrepaymentMode(combo?.prepaymentMode),
  depositType: combo?.depositType ? normalizeDepositType(combo.depositType) : DepositType.FixedAmount,
  depositValue: combo?.depositValue ?? '',
  allowInstallments: combo?.allowInstallments === true,
  ...readVatClassification(combo),
});

const toggleId = (ids, id) => (
  ids.some((currentId) => String(currentId) === String(id))
    ? ids.filter((currentId) => String(currentId) !== String(id))
    : [...ids, id]
);

const buildInputSx = (hasError = false) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : BRAND.line}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
  minHeight: 48,
});

const formatDurationLabel = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours.toString().padStart(2, '0')}h${remainingMinutes.toString().padStart(2, '0')}`;
};

const DURATION_OPTIONS = Array.from(
  { length: ((MAX_DURATION - MIN_DURATION) / DURATION_STEP) + 1 },
  (_, index) => {
    const minutes = MIN_DURATION + (index * DURATION_STEP);
    return { value: minutes, label: formatDurationLabel(minutes) };
  },
);

const toDurationSelectValue = (duration) => {
  const minutes = Number(duration);
  if (!Number.isInteger(minutes) || minutes < MIN_DURATION || minutes > MAX_DURATION || minutes % DURATION_STEP !== 0) {
    return '';
  }

  return minutes;
};

const durationMinutes = (service) => {
  const minutes = Number(service?.duration);
  return Number.isFinite(minutes) ? minutes : 0;
};

export const suggestComboTotals = (serviceIds, services) => {
  const selected = new Set((serviceIds || []).map(String));
  return (services || []).reduce((totals, service) => {
    if (!selected.has(String(service.id))) {
      return totals;
    }

    return {
      duration: totals.duration + durationMinutes(service),
      price: totals.price + (Number(service.price) || 0),
    };
  }, { duration: 0, price: 0 });
};

export const validateServiceComboForm = (combo) => {
  const errors = {};
  const nameValues = Object.values(combo.name || {});
  if (nameValues.length === 0 || nameValues.some((value) => !String(value || '').trim())) {
    errors.name = 'serviceCombosManagement.validation.name';
  }

  if (!Array.isArray(combo.serviceIds) || new Set(combo.serviceIds).size < 2) {
    errors.serviceIds = 'serviceCombosManagement.validation.services';
  }

  const minutes = Number(combo.duration);
  if (!Number.isInteger(minutes) || minutes < MIN_DURATION || minutes > MAX_DURATION || minutes % DURATION_STEP !== 0) {
    errors.duration = 'serviceCombosManagement.validation.duration';
  }

  const price = Number(combo.price);
  if (!Number.isFinite(price) || price < 0 || combo.price === '') {
    errors.price = 'serviceCombosManagement.validation.price';
  }

  const prepaymentError = !isFreeServicePrice(combo.price)
    ? validateServicePrepayment({ ...combo, price })
    : null;
  if (prepaymentError) {
    errors.prepayment = prepaymentError;
  }

  Object.assign(errors, validateVatClassification(combo));

  return errors;
};

const ServiceComboEditorDialog = ({
  open,
  mode,
  combo,
  services,
  supportedLanguages,
  currency,
  storeId,
  isPro = true,
  onlinePaymentsReady = false,
  installmentMethodsAvailable = false,
  saving,
  onClose,
  onSave,
}) => {
  const { t, i18n } = useTranslation();
  const languages = supportedLanguages?.length ? supportedLanguages : FALLBACK_LANGUAGES;
  const [language, setLanguage] = useState(languages[0]);
  const [form, setForm] = useState(() => buildInitialCombo(languages, combo));
  const [errors, setErrors] = useState({});
  const durationEdited = useRef(false);
  const priceEdited = useRef(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setLanguage(languages[0]);
    setForm(buildInitialCombo(languages, combo));
    setErrors({});
    durationEdited.current = false;
    priceEdited.current = false;
  }, [combo, open, languages]);

  const serviceOptions = useMemo(() => (
    (services || [])
      .filter((service) => !service.isCombo)
      .map((service) => ({
        id: service.id,
        label: getLocalizedValue(service.name, i18n.language, languages[0], t('serviceCombosManagement.card.unnamed')),
      }))
  ), [i18n.language, languages, services, t]);

  const handleSave = () => {
    const nextErrors = validateServiceComboForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    onSave({
      ...form,
      duration: Number(form.duration),
      price: Number(form.price),
    });
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      showCloseButton
      dividers
      title={t(mode === 'edit' ? 'serviceCombosManagement.dialog.editTitle' : 'serviceCombosManagement.dialog.createTitle')}
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button color="inherit" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton onClick={handleSave} disabled={saving}>
            {t('common.save')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: errors.name ? 'error.main' : 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={language}
            onChange={(_, next) => setLanguage(next)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              backgroundColor: '#f8f9fa',
              '& .MuiTab-root': {
                color: '#666',
                fontWeight: 600,
                textTransform: 'none',
                minHeight: 48,
              },
              '& .Mui-selected': {
                color: BRAND.accentText,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: BRAND.accent,
              },
            }}
          >
            {languages.map((code) => (
              <Tab
                key={code}
                value={code}
                label={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                    <LanguageFlag languageCode={code} size="1rem" />
                    <Typography variant="body2">{t(`common.languages.${code}`)}</Typography>
                  </Box>
                )}
              />
            ))}
          </Tabs>
          <Box sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {`${t('serviceCombosManagement.fields.name')} (${t(`common.languages.${language}`)}) *`}
            </Typography>
            <Box
              component="input"
              aria-label={`${t('serviceCombosManagement.fields.name')} (${t(`common.languages.${language}`)})`}
              value={form.name[language] || ''}
              onChange={(event) => setForm((current) => ({
                ...current,
                name: { ...current.name, [language]: event.target.value },
              }))}
              sx={buildInputSx(Boolean(errors.name))}
            />
            {errors.name ? (
              <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>{t(errors.name)}</Typography>
            ) : null}
          </Box>
        </Box>
        <ServicePackageCheckboxGroup
          id="service-combo-services"
          title={t('serviceCombosManagement.fields.services')}
          helpText={t('serviceCombosManagement.fields.servicesHelp')}
          emptyText={t('serviceCombosManagement.fields.noServices')}
          options={serviceOptions}
          selectedIds={form.serviceIds}
          onToggle={(serviceId) => setForm((current) => {
            const serviceIds = toggleId(current.serviceIds, serviceId);
            const suggestion = suggestComboTotals(serviceIds, services);
            return {
              ...current,
              serviceIds,
              duration: durationEdited.current ? current.duration : suggestion.duration,
              price: priceEdited.current ? current.price : suggestion.price,
            };
          })}
          error={errors.serviceIds}
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <SalovareSelectField
            label={t('serviceCombosManagement.fields.duration')}
            required
            error={Boolean(errors.duration)}
            helperText={errors.duration ? t(errors.duration) : undefined}
            value={toDurationSelectValue(form.duration)}
            displayEmpty
            onChange={(event) => {
              durationEdited.current = true;
              setForm((current) => ({ ...current, duration: Number(event.target.value) }));
            }}
          >
            {DURATION_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </SalovareSelectField>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={fieldLabelRowSx}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {`${t('serviceCombosManagement.fields.price')} *`}
              </Typography>
            </Box>
            <TextField
              type="number"
              value={form.price}
              onChange={(event) => {
                priceEdited.current = true;
                setForm((current) => ({ ...current, price: event.target.value }));
              }}
              inputProps={{
                min: 0,
                step: 0.01,
                'aria-label': t('serviceCombosManagement.fields.price'),
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {getCurrencySymbol(currency)}
                  </InputAdornment>
                ),
              }}
              error={Boolean(errors.price)}
              sx={buildAdornmentTextFieldSx(Boolean(errors.price))}
              fullWidth
            />
            {errors.price ? (
              <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>{t(errors.price)}</Typography>
            ) : null}
          </Box>
        </Box>
        {!isFreeServicePrice(form.price) ? (
          <ProLockedSection locked={!isPro}>
            <ServicePrepaymentFields
              value={{
                prepaymentMode: form.prepaymentMode,
                depositType: form.depositType,
                depositValue: form.depositValue,
              }}
              price={form.price}
              currency={currency}
              error={errors.prepayment}
              onlinePaymentsReady={onlinePaymentsReady}
              storeId={storeId}
              onChange={(prepayment) => setForm((current) => ({ ...current, ...prepayment }))}
            />
          </ProLockedSection>
        ) : null}
        {!isFreeServicePrice(form.price) && ((isPro && onlinePaymentsReady && installmentMethodsAvailable) || !isPro) ? (
          <ProLockedSection locked={!isPro}>
            <SwitchField
              containerSx={{ pl: 0, pr: 1 }}
              checked={form.allowInstallments === true}
              onChange={(event) => setForm((current) => ({ ...current, allowInstallments: event.target.checked }))}
              label={t('servicesManagement.prepayment.allowInstallments')}
            />
          </ProLockedSection>
        ) : null}
        <VatClassificationFields
          idPrefix="service-combo-vat"
          value={form}
          errors={errors}
          onChange={(next) => setForm((current) => ({ ...current, ...next }))}
        />
        <SwitchField
          id="service-combo-active"
          checked={!form.disabled}
          onChange={(event) => setForm((current) => ({ ...current, disabled: !event.target.checked }))}
          label={t('serviceCombosManagement.fields.active')}
        />
      </Box>
    </ScrollableDialog>
  );
};

export default ServiceComboEditorDialog;
